import React, { useMemo } from 'react';
import { ToolComponentProps } from './types';
import { ChevronRight, ChevronDown } from 'lucide-react';

interface ParsedError {
  groupId: string;
  timeSince: string;
  url?: string;
  error: string;
  stacktrace: string[];
}

const parseErrorOutput = (output: string): ParsedError[] => {
  const errors: ParsedError[] = [];
  const errorBlocks = output.split(/<vibe-error group_id="(\d+)">/g);

  for (let i = 1; i < errorBlocks.length; i += 2) {
    const groupId = errorBlocks[i];
    const content = errorBlocks[i + 1]?.split('</vibe-error>')[0];

    if (!content) continue;

    // Extract time since
    const timeMatch = content.match(/Seen: ([^(]+)/);
    const timeSince = timeMatch ? timeMatch[1].trim() : '';

    // Extract URL
    const urlMatch = content.match(/URL: ([^\n]+)/);
    const url = urlMatch ? urlMatch[1].trim() : undefined;

    // Extract error message
    const errorMatch = content.match(/Error: ([^\n]+)/);
    const error = errorMatch ? errorMatch[1].trim() : '';

    // Extract stack trace
    const stackMatch = content.match(/Stack \(first \d+ lines[^:]*\):\n([\s\S]*?)(?=\n\n|$)/);
    const stacktrace: string[] = [];
    if (stackMatch) {
      const stackLines = stackMatch[1].trim().split('\n');
      stackLines.forEach(line => {
        const trimmed = line.trim();
        if (trimmed) stacktrace.push(trimmed);
      });
    }

    errors.push({ groupId, timeSince, url, error, stacktrace });
  }

  return errors;
};

export const ErrorReportsListTool: React.FC<ToolComponentProps> = ({
  input,
  output,
  isError,
  isExpanded,
  onToggle
}) => {
  const limit = input?.limit || 10;
  const hasOutput = output !== undefined && output !== null;

  const parsedErrors = useMemo(() => {
    if (!output || typeof output !== 'string' || isError) return null;
    return parseErrorOutput(output);
  }, [output, isError]);

  return (
    <div>
      <div
        className={`flex items-center gap-1 ${hasOutput ? 'cursor-pointer hover:bg-accent/30' : ''} rounded transition-colors`}
        onClick={hasOutput ? onToggle : undefined}
      >
        <span className="flex-1">List Runtime Errors</span>
        <span className="text-muted-foreground inline-block w-3">
          {hasOutput && (isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />)}
        </span>
      </div>

      {isExpanded && hasOutput && (
        <div className={`mt-2 rounded`}>
          {isError ? (
            <div className="p-2 rounded bg-destructive/10 text-destructive">
              <pre className="whitespace-pre-wrap text-xs">
                {typeof output === 'string' ? output : JSON.stringify(output, null, 2)}
              </pre>
            </div>
          ) : parsedErrors ? (
            <div className="bg-muted rounded p-2">
              <pre className="whitespace-pre-wrap text-xs">
                {parsedErrors.map((err, idx) => {
                  let output = `- ${err.groupId} (${err.timeSince})\n`;
                  if (err.url) output += `  URL: ${err.url}\n`;
                  output += `  Error: ${err.error}\n`;
                  if (err.stacktrace.length > 0) {
                    output += '  Stacktrace:\n';
                    err.stacktrace.forEach(line => {
                      output += `    ${line}\n`;
                    });
                  }
                  if (idx < parsedErrors.length - 1) output += '\n';
                  return output;
                }).join('')}
              </pre>
            </div>
          ) : (
            <div className="bg-muted rounded p-2">
              <pre className="whitespace-pre-wrap text-xs">
                {typeof output === 'string' ? output : JSON.stringify(output, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
